import React, {useState, useEffect, useRef} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './TaskDetails.css';
import '../../Dashboards/MoreSelectionMenu.css';
import '../../../components/Selections/Dropdowns/SelectionDropdown.css';
import {ReactComponent as MoreIcon} from '../../../assets/icons/more-icon.svg'
import {ReactComponent as BoltIcon} from '../../../assets/icons/bolt-icon.svg'
import { CHAT_MODES } from '../../Threads/helpers/modeConfigs';
import { ReactComponent as DownIcon } from '../../../assets/icons/down-icon.svg';
import axios from 'axios';
import _ from 'lodash';
import Popup from '../../../components/Popup/Popup';
import LoadingScreen from '../../../components/LoadingScreen/LoadingScreen';


// Cron calculation utility (client-side simulation)
const calculateNextRunFromCron = (cronExpression, baseDate = new Date()) => {
    if (!cronExpression) return null;

    try {
        const parts = cronExpression.trim().split(/\s+/);
        if (parts.length !== 5) return null;

        const [minuteStr, hourStr, dayOfMonthStr, _monthStr, dayOfWeekStr] = parts;
        const minute = parseInt(minuteStr, 10);
        const hour = parseInt(hourStr, 10);
        const now = new Date(baseDate);

        const makeDate = (year, monthIndex, dayOfMonth) => {
            return new Date(year, monthIndex, dayOfMonth, hour, minute, 0, 0);
        };

        // Handle weekly recurrence
        if (dayOfWeekStr !== '*') {
            const targetDays = dayOfWeekStr
                .split(',')
                .map(d => {
                    const val = parseInt(d, 10);
                    if (val === 7) return 0; // Support both 0 and 7 for Sunday
                    return isNaN(val) ? null : val;
                })
                .filter(d => d !== null);

            let nextRun = null;
            for (const targetDay of targetDays) {
                const candidate = new Date(now);
                candidate.setHours(hour, minute, 0, 0);

                const currentDay = candidate.getDay();
                let daysUntilNext = (targetDay - currentDay + 7) % 7;

                // If it's today but time has passed/equal, schedule for next week
                if (daysUntilNext === 0 && candidate <= now) {
                    daysUntilNext = 7;
                }

                candidate.setDate(candidate.getDate() + daysUntilNext);

                if (!nextRun || candidate < nextRun) {
                    nextRun = candidate;
                }
            }
            return nextRun ? nextRun.getTime() : null;
        }

        // Handle monthly recurrence
        if (dayOfMonthStr !== '*') {
            const targetDay = parseInt(dayOfMonthStr, 10);
            if (isNaN(targetDay) || targetDay < 1 || targetDay > 31) return null;

            let year = now.getFullYear();
            let monthIndex = now.getMonth();

            // Search the current month forward until a valid month/day is found in the future
            for (let i = 0; i < 24; i++) { // cap to avoid infinite loop
                const candidate = makeDate(year, monthIndex, targetDay);

                // If invalid day for this month, JS rolls over to next month.
                // Detect rollover and skip this month.
                if (candidate.getMonth() !== monthIndex || candidate.getDate() !== targetDay) {
                    monthIndex += 1;
                    if (monthIndex > 11) {
                        monthIndex = 0;
                        year += 1;
                    }
                    continue;
                }

                if (candidate > now) {
                    return candidate.getTime();
                }

                // Otherwise, move to next month and try again
                monthIndex += 1;
                if (monthIndex > 11) {
                    monthIndex = 0;
                    year += 1;
                }
            }
            return null;
        }

        // Handle daily recurrence
        const candidate = new Date(now);
        candidate.setHours(hour, minute, 0, 0);

        // If time has passed/equal today, schedule for tomorrow
        if (candidate <= now) {
            candidate.setDate(candidate.getDate() + 1);
        }

        return candidate.getTime();
    } catch (error) {
        console.error('Error calculating next run:', error);
        return null;
    }
};

const TaskDetails = ({task, setTasks, updateTask, runTask, deleteTask, createTask, setModelOpen, isNewTask, defaultLoading = false}) => {
    const [localTask, setLocalTask] = useState(task);
    const [isSaved, setIsSaved] = useState(true);
    const [moreSelectionOpen, setMoreSelectionOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [loading, setLoading] = useState(defaultLoading);
    const [error, setError] = useState(null);
    const [editingNextRun, setEditingNextRun] = useState(false);
    const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
    const [runLoading, setRunLoading] = useState(false);
    const [agentMenuOpen, setAgentMenuOpen] = useState(false);
    const objectiveTextareaRef = useRef(null);
    const emailsInputRef = useRef(null);
    // Derive agent options from CHAT_MODES configuration
    const agentOptions = Object.values(CHAT_MODES)
        .filter(mode => mode.canExecuteTasks)
        .map(mode => ({
            key: mode.key,
            label: mode.label,
            desc: mode.desc,
            themeColor: mode.themeColor,
            iconElement: mode?.greeting?.icon || null,
        }));
    
    // Recurrence state
    const [recurrenceSettings, setRecurrenceSettings] = useState({
        frequency: 'weekly', // 'once', 'daily', 'weekly', 'monthly'
        time: '09:00',
        selectedDays: [5], // Monday-Friday by default for weekly
        monthDay: 1 // For monthly recurrence
    });
    
    // Guard to prevent ping-pong updates between cron <-> settings
    const isApplyingSettingsRef = useRef(false);
    
    const navigate = useNavigate();

    // Update loading state when defaultLoading prop changes
    useEffect(() => {
        setLoading(defaultLoading);
    }, [defaultLoading]);

    // Auto-resize objective textarea up to 3 lines
    const resizeObjectiveTextarea = () => {
        const el = objectiveTextareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        const computed = window.getComputedStyle(el);
        const lineHeight = parseFloat(computed.lineHeight) || 20;
        const paddingTop = parseFloat(computed.paddingTop) || 0;
        const paddingBottom = parseFloat(computed.paddingBottom) || 0;
        const borderTop = parseFloat(computed.borderTopWidth) || 0;
        const borderBottom = parseFloat(computed.borderBottomWidth) || 0;
        const singleLineHeight = lineHeight + paddingTop + paddingBottom + borderTop + borderBottom;
        const maxHeight = lineHeight * 3 + paddingTop + paddingBottom + borderTop + borderBottom;
        const contentHeight = el.scrollHeight + borderTop + borderBottom; // scrollHeight includes padding
        const newHeight = Math.max(singleLineHeight, Math.min(contentHeight, maxHeight));
        el.style.height = `${newHeight}px`;
        el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
    };

    useEffect(() => {
        resizeObjectiveTextarea();
        // Also resize on window resize to handle font/zoom changes
        const onResize = () => resizeObjectiveTextarea();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [localTask?.objective]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (agentMenuOpen && !event.target.closest('.agent-select')) {
                setAgentMenuOpen(false);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [agentMenuOpen]);

    useEffect(() => {
        if (!_.isEqual(localTask, task)) {
            setIsSaved(false);
        } else { 
            setIsSaved(true);
        }
    }, [localTask]);

    useEffect(() => {
        if (task && isNewTask) {
            setLocalTask(prev => ({...prev, ...task}));
        }
    }, [task, isNewTask]);
    
    // Initialize recurrence settings from cron when task changes
    useEffect(() => {
        if (!localTask?.cron) return;
        if (isApplyingSettingsRef.current) return; // Skip if we're applying settings->cron
        const settings = parseCronToSettings(localTask.cron);
        setRecurrenceSettings(prev => (_.isEqual(prev, settings) ? prev : settings));
    }, [localTask?.cron]);
    
    // Update cron and next_run when recurrence settings change
    useEffect(() => {
        const cronExpression = buildCronFromSettings(recurrenceSettings);
        let computedNextRun = localTask?.next_run ?? null;

        if (recurrenceSettings.frequency === 'once') {
            // Preserve existing next_run for one-time tasks, or default to tomorrow only once
            computedNextRun = localTask?.next_run || (Date.now() + (24 * 60 * 60 * 1000));
        } else if (cronExpression) {
            // Always compute from "now" to get the soonest upcoming occurrence
            computedNextRun = calculateNextRunFromCron(cronExpression, new Date());
        }

        const cronChanged = (cronExpression ?? null) !== (localTask?.cron ?? null);
        const nextRunChanged = typeof computedNextRun === 'number' && computedNextRun !== localTask?.next_run;

        if (cronChanged || nextRunChanged) {
            isApplyingSettingsRef.current = true;
            setLocalTask(prev => ({
                ...prev,
                cron: cronExpression,
                next_run: computedNextRun
            }));
            // Reset guard after state update completes
            setTimeout(() => {
                isApplyingSettingsRef.current = false;
            }, 0);
        }
    }, [recurrenceSettings]);

    const handleSaveClicked = () => {
        if (!_.isEqual(localTask, task)) {
            // Create updates object with only changed fields
            const updates = {};
            Object.keys(localTask).forEach(key => {
                if (!_.isEqual(localTask[key], task[key])) {
                    updates[key] = localTask[key];
                }
            });
            updateTask(task.id, updates);
            setModelOpen(false);
        }
    }

    function formatDateTimeLocal(timestamp) {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        const pad = n => n.toString().padStart(2, '0');
        const year = date.getFullYear();
        const month = pad(date.getMonth() + 1);
        const day = pad(date.getDate());
        const hours = pad(date.getHours());
        const minutes = pad(date.getMinutes());
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    const handleCancelClicked = () => {
        setModelOpen(false);
    }

    const handleToggleAccessType = () => {
        if (localTask?.sendEmail) {
            setLocalTask({...localTask, sendEmail: false});
        } else {
            // Ensure emailRecipients array exists when enabling
            const existing = Array.isArray(localTask?.emailRecipients)
              ? localTask.emailRecipients
              : [];
            setLocalTask({...localTask, sendEmail: true, emailRecipients: existing});
        }
    }

    const handleEmailsInputChange = (e) => {
        const value = e.target.value || '';
        const emails = value
            .split(',')
            .map(v => v.trim())
            .filter(v => v.length > 0);
        setLocalTask(prev => ({
            ...prev,
            emailRecipients: emails
        }));
    };

    // Inline edit state for email recipients
    const [isEditingEmails, setIsEditingEmails] = useState(false);
    const [emailsDraft, setEmailsDraft] = useState('');

    const openEmailsEditor = () => {
        const current = Array.isArray(localTask?.emailRecipients)
            ? localTask.emailRecipients.join(', ')
            : '';
        setEmailsDraft(current);
        setIsEditingEmails(true);
        // Focus after state update
        setTimeout(() => emailsInputRef.current && emailsInputRef.current.focus(), 0);
    };

    const saveEmailsEditor = () => {
        const emails = (emailsDraft || '')
            .split(',')
            .map(v => v.trim())
            .filter(v => v.length > 0);
        setLocalTask(prev => ({ ...prev, emailRecipients: emails }));
        setIsEditingEmails(false);
    };

    const cancelEmailsEditor = () => {
        setIsEditingEmails(false);
        setEmailsDraft('');
    };

    const handleToggleStatus = () => {
        const newStatus = localTask?.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
        setLocalTask({...localTask, status: newStatus});
    }

    const getButtonTextByStatus = (status) => {
        switch (status) {
            case 'INACTIVE':
                return 'Paused';
            case 'ACTIVE':
                return 'Run Now';
            default:
                return 'Paused';
        }
    }

    const handleConfirmDeleteClicked = async () => {
        try {
            setConfirmationOpen(false);
            await deleteTask({taskId: localTask?.id});            
        } catch (error) {
            console.error('Error deleting task:', error);
        } finally {
            setLoading(false);
        }
    }

    const getStatusDisplayText = (status) => {
        switch (status) {
            case 'ACTIVE':
                return 'Active';
            case 'INACTIVE':
                return 'Paused';
            default:
                return 'Paused';
        }
    };

    // Utility functions for cron conversion
    const parseCronToSettings = (cronExpression) => {
        if (!cronExpression) {
            return { frequency: 'once', time: '09:00', selectedDays: [], monthDay: 1 };
        }
        
        const parts = cronExpression.split(' ');
        if (parts.length !== 5) {
            return { frequency: 'daily', time: '09:00', selectedDays: [1, 2, 3, 4, 5], monthDay: 1 };
        }
        
        const [minute, hour, day, month, dayOfWeek] = parts;
        const timeStr = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
        
        if (dayOfWeek !== '*') {
            // Weekly recurrence
            const days = dayOfWeek.split(',').map(d => parseInt(d)).filter(d => !isNaN(d));
            return {
                frequency: 'weekly',
                time: timeStr,
                selectedDays: days,
                monthDay: 1
            };
        } else if (day !== '*') {
            // Monthly recurrence
            return {
                frequency: 'monthly',
                time: timeStr,
                selectedDays: [],
                monthDay: parseInt(day)
            };
        } else {
            // Daily recurrence
            return {
                frequency: 'daily',
                time: timeStr,
                selectedDays: [],
                monthDay: 1
            };
        }
    };
    
    const buildCronFromSettings = (settings) => {
        if (settings.frequency === 'once') {
            return null;
        }
        
        const [hour, minute] = settings.time.split(':').map(n => parseInt(n));
        
        switch (settings.frequency) {
            case 'daily':
                return `${minute} ${hour} * * *`;
            case 'weekly':
                if (settings.selectedDays.length === 0) return `${minute} ${hour} * * *`;
                return `${minute} ${hour} * * ${settings.selectedDays.join(',')}`;
            case 'monthly':
                return `${minute} ${hour} ${settings.monthDay} * *`;
            default:
                return `${minute} ${hour} * * *`;
        }
    };
    
    const translateCronToEnglish = (cronExpression) => {
        if (!cronExpression) return 'Run once';
        
        const parts = cronExpression.split(' ');
        if (parts.length !== 5) return cronExpression;
        
        const [minute, hour, day, month, dayOfWeek] = parts;
        const timeStr = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;
        
        if (dayOfWeek !== '*') {
            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const days = dayOfWeek.split(',').map(d => dayNames[parseInt(d)]).join(', ');
            return `Weekly on ${days} at ${timeStr}`;
        } else if (day !== '*') {
            const suffix = day.endsWith('1') && day !== '11' ? 'st' : 
                         day.endsWith('2') && day !== '12' ? 'nd' : 
                         day.endsWith('3') && day !== '13' ? 'rd' : 'th';
            return `Monthly on ${day}${suffix} at ${timeStr}`;
        } else {
            return `Daily at ${timeStr}`;
        }
    };

    const handleCreateTask = async () => {
        // ensure agent selected
        if (!localTask?.agent) {
            setError('Please select an agent');
            return;
        }
        // check if all fields are filled
        if (!localTask?.title || !localTask?.objective || !localTask?.cron || !localTask?.next_run || !localTask?.workspaceId || !localTask?.creatorUserId) {
            setError('Please fill in all fields');
            return;
        }
        // check if next run is in the future
        if (localTask?.next_run < Date.now()) {
            return;
        }
        try {
            setLoading(true);
            await createTask({task: localTask});
        } catch (error) {
            console.error('Error creating task:', error);
            // Check for Axios error structure
            if (error.message || error.response || error.response.data || error.response.data.message === 'No matching data source found') {
                console.log("Error.");
                setError('No matching data source found');
            }
        } finally {
            setLoading(false);
        }
    }

    const handleRunTask = async () => {
        if (localTask?.status === 'ACTIVE') {
            setRunLoading(true);
            try {
                const result = await runTask(localTask?.id); // Assume result has .id
                setShowSuccessAnimation(true);
                setTimeout(() => {
                    setShowSuccessAnimation(false);
                    setModelOpen(false);
                    if (result && result.id) {
                        navigate(`/insights/${result.id}`);
                    } else {
                        navigate('/insights');
                    }
                }, 800); // brief success pulse
            } finally {
                setRunLoading(false);
            }
        }
    };

    ////////////////////// render functions //////////////////////
    const renderMoreSelection = () => (
        <div className="more-selection-menu">
            <div className="more-option" onClick={(event) => {
                event.stopPropagation();
                setLocalTask({...localTask, cron: '0 9 * * *'}); // Daily at 9 AM
                setMoreSelectionOpen(false);
            }}>
                <span>Daily at 9:00 AM</span>
                {/* <span className="pro-badge">Pro</span> */}
            </div>
            <div className="more-option" onClick={(event) => {
                event.stopPropagation();
                setLocalTask({...localTask, cron: '0 9 * * 1'}); // Weekly on Monday at 9 AM
                setMoreSelectionOpen(false);
            }}>
                <span>Weekly on Monday at 9:00 AM</span>
            </div>
            <div className="more-option" onClick={(event) => {
                event.stopPropagation();
                setLocalTask({...localTask, cron: '0 9 1 * *'}); // Monthly on 1st at 9 AM
                setMoreSelectionOpen(false);
            }}>
                <span>Monthly on 1st at 9:00 AM</span>
            </div>
        </div>
    );

    const renderMenu = () => (
        <div className="more-selection-menu">
            <div className="more-option" onClick={(event) => {
                event.stopPropagation();
                handleToggleStatus();
                setMenuOpen(false);
            }}>
                <span>{localTask?.status === 'ACTIVE' ? 'Pause Task' : 'Unpause'}</span>
                {/* <span className="pro-badge">Pro</span> */}
            </div>
            <div className="more-option delete" onClick={(event) => {
                event.stopPropagation();
                setConfirmationOpen(true);
            }}>
                <span>Delete Task</span>
            </div>
        </div>
    );

    const renderConfirmation = () => (
        <Popup
            content={
                <div className="confirmation-popup">
                    <div className="confirmation-popup-content">
                        <>
                            <h2>Are you sure you want to delete this task?</h2>
                            <p>Deleting will stop all future runs for this agent. You can always re-create it at any time.</p>
                        </>
                        <div className="actions">
                            <div className="action cancel" onClick={() => setConfirmationOpen(false)}>
                                <span>Cancel</span>
                            </div>
                            <div className="action confirm" onClick={handleConfirmDeleteClicked}>
                                <span>Delete</span>
                            </div>
                        </div>
                    </div>
                </div>
            }
            onClose={() => setConfirmationOpen(false)}
        >
        </Popup>
    );


    return (
        <div className={`task-details-wrapper ${loading ? 'loading' : ''}`}>
            {loading && <LoadingScreen message="Configuring task..." />}
            <div className="section header">
                <div className='avatar'>
                    <BoltIcon className='icon' />
                </div>
                <div className="header-titles">
                    <input
                        type="text"
                        value={localTask?.title || ''}
                        onChange={e => setLocalTask({...localTask, title: e.target.value})}
                        placeholder="Untitled Task"
                        className="title"
                    />
                    <div className="subtitle">
                        <div className="agent-select" onClick={(e) => { e.stopPropagation(); setAgentMenuOpen(!agentMenuOpen); }}>
                            <div className="agent-select-display">
                                <span>{localTask?.agent || 'Select agent'}</span>
                            </div>
                            <DownIcon className="down-icon" />
                            {agentMenuOpen && (
                                <div className="more-selection-menu" style={{ left: '0', top: 'calc(100% + 8px)', right: 'auto', bottom: 'auto', zIndex: 1000, minWidth: '320px' }}>
                                    {agentOptions.map((opt) => (
                                        <div
                                            key={opt.key}
                                            className={`more-option`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setLocalTask(prev => ({...prev, agent: opt.label}));
                                                setAgentMenuOpen(false);
                                            }}
                                        >
                                            <div className={`mode-icon-wrapper`} style={{ backgroundColor: opt.themeColor }}>
                                                {opt.iconElement && React.cloneElement(opt.iconElement, { className: 'mode-icon' })}
                                            </div>
                                            <div className="mode-texts">
                                                <div className="mode-label" style={{ fontFamily: 'var(--font-bold)', color: 'var(--grey-medium-color)', fontSize: '14px' }}>{opt.label}</div>
                                                {opt.desc && <div className="mode-desc" style={{ fontSize: '12px', color: 'var(--grey-medium-color)', marginTop: '2px', lineHeight: '1.2' }}>{opt.desc}</div>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                {!isNewTask && (
                    <div className="more" onClick={(event) => {
                        event.stopPropagation();
                        setMenuOpen(!menuOpen);
                    }}>
                        <MoreIcon className="icon" />
                    </div>
                )}
                {menuOpen && renderMenu()}
                {confirmationOpen && renderConfirmation()}
            </div>
            <hr className="divider" />
            <div className="section metadata content">
                <div className="item next-run">
                    <h2>Next Run</h2>
                    <div className="content calculated-next-run">
                        {(() => {
                            const status = (localTask?.status || '').toString().toUpperCase();
                            const isInactive = status === 'INACTIVE' || !localTask?.next_run;
                            return (
                                <span className={`next-run-display${isInactive ? ' inactive' : ''}`}>
                                    {isInactive
                                        ? 'Paused'
                                        : new Date(localTask?.next_run).toLocaleString('en-US', {
                                            weekday: 'short',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: 'numeric',
                                            minute: '2-digit',
                                            hour12: true
                                        })}
                                </span>
                            );
                        })()}
                    </div>
                </div>
                <div className="item recurrence">
                    {/* <h2>Recurrence</h2> */}
                    <div className="content recurrence-controls">
                        <div className="recurrence-row">
                            <div className="recurrence-section frequency-section">
                                <label>Frequency</label>
                                <select 
                                    value={recurrenceSettings.frequency}
                                    onChange={e => setRecurrenceSettings(prev => ({...prev, frequency: e.target.value}))}
                                    className="frequency-select"
                                >
                                    <option value="once">Run once</option>
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                </select>
                            </div>
                            
                            {recurrenceSettings.frequency !== 'once' && (
                                <div className="recurrence-section time-section">
                                    <label>Time</label>
                                    <input
                                        type="time"
                                        value={recurrenceSettings.time}
                                        onChange={e => setRecurrenceSettings(prev => ({...prev, time: e.target.value}))}
                                        className="time-input"
                                    />
                                </div>
                            )}
                        </div>
                        
                        {recurrenceSettings.frequency === 'once' && (
                            <div className="recurrence-section datetime-section">
                                <label>Run Date & Time</label>
                                <input
                                    type="datetime-local"
                                    value={formatDateTimeLocal(localTask?.next_run)}
                                    onChange={e => {
                                        const value = e.target.value;
                                        const timestamp = value ? new Date(value).getTime() : '';
                                        setLocalTask(prev => ({...prev, next_run: timestamp}));
                                    }}
                                    className="datetime-input"
                                />
                            </div>
                        )}
                        
                        {recurrenceSettings.frequency === 'weekly' && (
                            <div className="recurrence-section days-section">
                                <label>Select Days</label>
                                <div className="days-selector">
                                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, index) => {
                                                const dayNum = index === 6 ? 0 : index + 1; // Sunday = 0 in cron
                                        return (
                                            <button
                                                key={day}
                                                type="button"
                                                className={`day-button ${recurrenceSettings.selectedDays.includes(dayNum) ? 'selected' : ''}`}
                                                onClick={() => {
                                                    const newDays = recurrenceSettings.selectedDays.includes(dayNum)
                                                        ? recurrenceSettings.selectedDays.filter(d => d !== dayNum)
                                                        : [...recurrenceSettings.selectedDays, dayNum].sort();
                                                    setRecurrenceSettings(prev => ({...prev, selectedDays: newDays}));
                                                }}
                                            >
                                                {day}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        
                        {recurrenceSettings.frequency === 'monthly' && (
                            <div className="recurrence-section month-day-section">
                                <label>Day of Month</label>
                                <select 
                                    value={recurrenceSettings.monthDay}
                                    onChange={e => setRecurrenceSettings(prev => ({...prev, monthDay: parseInt(e.target.value)}))}
                                    className="month-day-select"
                                >
                                    {Array.from({length: 31}, (_, i) => i + 1).map(day => (
                                        <option key={day} value={day}>{day}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        
                        {/* <div className="recurrence-summary">
                            <span className="summary-text">{translateCronToEnglish(localTask.cron)}</span>
                        </div> */}
                    </div>
                </div>
            </div>
            <hr className="divider short" />
            <div className="section settings content">
                <h2>Settings</h2>
                <div className="item email">
                    <div className="text">
                        <p>Send email notification upon completion</p>
                    </div>
                    <div className={`toggle ${localTask?.sendEmail ? 'enabled' : 'disabled'}`} onClick={handleToggleAccessType} >
                        <div className='slider'></div>
                    </div>
                </div>
                {localTask?.sendEmail && (
                    <div className="item email-recipients">
                        <div className="text" style={{ width: '100%' }}>
                            {!isEditingEmails ? (
                                <div
                                    className={`email-editable-display ${Array.isArray(localTask?.emailRecipients) && localTask.emailRecipients.length > 0 ? 'has-emails' : ''}`}
                                    role="button"
                                    tabIndex={0}
                                    onClick={openEmailsEditor}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            openEmailsEditor();
                                        }
                                    }}
                                >
                                    <div className="email-recipients-list">
                                        {Array.isArray(localTask?.emailRecipients) && localTask.emailRecipients.length > 0 ? (
                                            <ul className="email-list">
                                                {localTask.emailRecipients.map((email, idx) => (
                                                    <li key={`${email}-${idx}`} className="email-item">{email}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <div className="email-empty">Add recipients</div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                <input
                                    ref={emailsInputRef}
                                    className="email-input"
                                    type="text"
                                    placeholder="name@example.com, other@example.com"
                                    value={emailsDraft}
                                    onChange={(e) => setEmailsDraft(e.target.value)}
                                    onBlur={saveEmailsEditor}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            saveEmailsEditor();
                                        } else if (e.key === 'Escape') {
                                            e.preventDefault();
                                            cancelEmailsEditor();
                                        }
                                    }}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
            <hr className="divider short" />
            <div className="section objective content">
                <div className="item objective">
                    <textarea
                        className="objective-input"
                        ref={objectiveTextareaRef}
                        value={localTask?.objective || ''}
                        placeholder="Task Objective"
                        onChange={e => {
                            const value = e.target.value.slice(0, 1000); // Limit to 1000 chars
                            setLocalTask({...localTask, objective: value});
                            // Let React update value, then resize
                            requestAnimationFrame(resizeObjectiveTextarea);
                        }}
                        maxLength={1000} // Enforce max length at the input level
                        rows={1}
                    />
                </div>
            </div>
            <div className="section note content">
                <div className="item note">
                    <textarea
                        className="note-input"
                        value={localTask?.notes || ''}
                        placeholder="Additional notes for the agent to follow as it executes the task."
                        onChange={e => {
                            const value = e.target.value.slice(0, 2000); // Limit to 1000 chars
                            setLocalTask({...localTask, notes: value});
                        }}
                        maxLength={2000} // Enforce max length at the input level
                        rows={3}
                    />
                </div>
            </div>
            {error && <div className="error">{error}</div>}
            {!isNewTask ? (
                <div className="section footer content">
                    <div className="footer-left">
                        
                    </div>
                    <div className="footer-right">
                        <button className="button cancel" onClick={handleCancelClicked}>Cancel</button>
                        <button className={`button save ${isSaved ? 'saved' : ''}`} onClick={handleSaveClicked}>{isSaved ? 'Saved' : 'Save'}</button>
                    </div>
                </div>
            ) : (
                <div className="section footer create content">
                    <button className="button create" onClick={handleCreateTask}>Schedule Task</button>
                </div>
            )}
        </div>
    );
};

export default TaskDetails;
